import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import { Link } from "src/components/routing";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { CommitteeMembersBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { findLookupValue } from "src/helpers/lookupHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as CommitteeMemberActions from "../../../actions/committeeMember";
import * as CommitteeActions from "../../../actions/committees";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { CommitteeSubMenu } from "../../../helpers/subMenuHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./CommitteeMembersPage.scss";

interface ICommitteeMembersPageParams {
    committeeId: number;
}

interface ICommitteeMembersPageProps
    extends IRouterProps<ICommitteeMembersPageParams> {
    committeeId: number;

    // From state
    formFilter: IFormFilter;
    committee: Dtos.Committee;
    committeeMembers: Dtos.CommitteeMember[] | undefined;
    lookups: Dtos.Lookup[];

    loadingCommittee: boolean;
    loadCommitteeSuccess: boolean;
    loadCommitteeFailure: boolean;
    loadCommitteeErrorCode: string;

    loadingCommitteeMembers: boolean;
    loadCommitteeMembersSuccess: boolean;
    loadCommitteeMembersFailure: boolean;
    loadCommitteeMembersErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];

    pageSize: number;
}

interface ICommitteeMembersPageActions extends IRouterActions {
    clearCommitteeMembers: CommitteeMemberActions.ICommitteeMemberClearActionCreator;
    loadCommitteeMembers: CommitteeMemberActions.ICommitteeMemberLoadByCommitteeIdActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

    clearCommittee: CommitteeActions.ICommitteeClearActionCreator;
    loadCommittee: CommitteeActions.ICommitteeLoadByIdActionCreator;

}

type CommitteeMembersPageProps = ICommitteeMembersPageProps &
    ICommitteeMembersPageActions;

class CommitteeMembersPage extends React.PureComponent<
    CommitteeMembersPageProps,
    any
> {
    initialised: boolean;

    constructor(props: CommitteeMembersPageProps) {
        super(props);
        this.initialised = false;
        this.clearCommittee = this.clearCommittee.bind(this);
        this.toggleExportMenu = this.toggleExportMenu.bind(this);

        this.state = {
            exportToggle: false,
            showCeased: false,
        };
    }

    componentDidMount() {
        const {
            loadCommitteeMembers,
            committeeId,
            permissions,
            setDrawerItems,
            setBreadcrumbs,
            loadCommittee
        } = this.props;
        

        if (committeeId) {
            setDrawerItems(CommitteeSubMenu(committeeId, permissions));
            setBreadcrumbs(CommitteeMembersBreadcrumbs(committeeId));
            loadCommittee(committeeId);
            loadCommitteeMembers(committeeId);
        }
    }

    componentDidUpdate(prevProps: CommitteeMembersPageProps, _prevState) {
        const {
            committee,
            committeeId,
            loadCommittee,
            setDrawerItems,
            setBreadcrumbs,
            permissions,
            loadCommitteeFailure,
            loadCommitteeMembersFailure,
            loadCommitteeErrorCode,
            loadCommitteeMembersErrorCode,
            navigate,
        } = this.props;

        if (loadCommitteeFailure) {
            navigate(`/error/${loadCommitteeErrorCode}`);
        }
        if (loadCommitteeMembersFailure) {
            navigate(`/error/${loadCommitteeMembersErrorCode}`);
        }

        if (committeeId && committee?.name) {
            setDrawerItems(CommitteeSubMenu(committeeId, permissions));
            setBreadcrumbs(
                CommitteeMembersBreadcrumbs(committeeId, committee.name)
            );
        }

        if (committeeId && prevProps.committeeId != committeeId) {
            loadCommittee(committeeId);
        }
    }

    componentWillUnmount() {
        this.clearCommittee();
    }

    clearCommittee() {
        this.props.clearCommittee();
        this.props.clearCommitteeMembers();
    }

    toggleExportMenu() {
        this.setState({
            exportToggle: !this.state.exportToggle,
        });
    }

    showMembers(): boolean {
        const { loadingCommitteeMembers, loadCommitteeMembersSuccess } =
            this.props;

        if (!loadingCommitteeMembers && loadCommitteeMembersSuccess) {
            return true;
        }

        return false;
    }

    onChange = (newValue) => {
        this.setState({ showCeased: newValue });
    };

    getCount(committeeMembers: Dtos.CommitteeMember[], type: string): number {
        if (!committeeMembers) return 0;

        switch (type) {
            case "current":
                return committeeMembers.filter((member) =>
                    member.committeeMemberRoles?.some(
                        (role) => !role.dateCeased
                    )
                ).length;

            case "pending":
                return committeeMembers.reduce(
                    (total, member) =>
                        total +
                        (member.committeeMemberRoles?.filter(
                            (role) =>
                                role.committeeMemberRoleTypeId === 8 &&
                                !role.dateCeased
                        ).length || 0),
                    0
                );

            case "ceased":
                return committeeMembers.reduce((ceasedTotal, member) => {
                    const roles = member.committeeMemberRoles || [];
                    const totalCeased = roles.filter(
                        (role) => role.dateCeased
                    ).length;
                    return totalCeased === roles.length
                        ? ceasedTotal + 1
                        : ceasedTotal;
                }, 0);

            default:
                return 0;
        }
    }

    getFontClass(committeeMember: Dtos.CommitteeMember): string {
        if (!committeeMember) return "current";

        if (
            committeeMember.committeeMemberRoles?.some(
                (role) =>
                    !role.dateCeased && role.committeeMemberRoleTypeId !== 8
            )
        ) {
            return "current";
        }

        if (
            committeeMember.committeeMemberRoles?.filter(
                (role) =>
                    role.committeeMemberRoleTypeId === 8 && !role.dateCeased
            ).length > 0
        ) {
            return "pending";
        }

        const totalCeased = committeeMember.committeeMemberRoles.filter(
            (role) => role.dateCeased
        ).length;
        if (totalCeased === committeeMember.committeeMemberRoles.length) {
            return "ceased";
        }
    }

    render() {
        const {
            loadingCommittee,
            loadingCommitteeMembers,
        } = this.props;

        return (
            <RestrictedLayout
                permission={Dtos.Permission.CommitteeMemberView}
                loading={loadingCommittee || loadingCommitteeMembers}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const { showCeased } = this.state;
        const { permissions, committeeId, committeeMembers } = this.props;

        const canCreate = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.CommitteeMemberCreate
        );

        return (
            <div>
                <div className="row">
                    <div className="col-4 mb-2">
                        <h1>Members</h1>
                    </div>
                    <div className="col-4 d-flex align-items-center justify-content-around mb-2">
                        <span className="current">
                            {this.getCount(committeeMembers, "current")} Current Member(s)
                        </span>
                        <span className="pending">
                            {this.getCount(committeeMembers, "pending")} Pending Member(s)
                        </span>
                        {showCeased && (
                            <span className="ceased">
                                {this.getCount(committeeMembers, "ceased")} Ceased Member(s)
                            </span>
                        )}
                    </div>
                    <div className="col-4 d-flex align-items-center justify-content-end mb-2">
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={this.onChange}
                            labelA="Hide ceased members"
                            labelB="Show ceased members"
                            style={{ minWidth: "200px" }}
                        />
                        {canCreate && (
                            <div style={{ paddingLeft: "8px" }}>
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/committees/${committeeId}/members/create`}
                                >
                                    Create
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
                {!this.showMembers() ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {this.showMembers() ? (
                    <div className="card">
                        <div className="patient-tables">
                            {this.createTable()}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    createTable() {
        const { showCeased } = this.state;
        const {
            committeeId,
            committeeMembers,
            pageSize,
            setPageSize,
            lookups,
        } = this.props;

        let members = [];
        if (showCeased) {
            members = committeeMembers;
        } else {
            members = committeeMembers?.filter((x) =>
                x.committeeMemberRoles?.find((x) => !x.dateCeased)
            );
        }

        if (members?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no members to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = members?.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={members}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : members?.length
                    }
                    pageSize={
                        showPagination ? pageSize : members?.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no members to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url = `/committees/${committeeId}/members/${rowInfo.original.id}/details`;
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "personnel.display",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (props) => (
                                <span
                                    className={this.getFontClass(
                                        props.original
                                    )}
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={FontAwesomeIcons.Duotone.USER}
                                        fixedWidth
                                    />
                                </span>
                            ),
                        },
                        {
                            id: "personnel.displayWithSalutation",
                            Header: "Name",
                            accessor: "personnel.displayWithSalutation",
                            Cell: (props) => (
                                <span
                                    className={this.getFontClass(
                                        props.original
                                    )}
                                >
                                    {props.value}
                                </span>
                            ),
                        },
                        {
                            id: "committeeMemberRoles",
                            Header: "Position",
                            accessor: "committeeMemberRoles",
                            Cell: (props) => (
                                <span
                                    className={this.getFontClass(
                                        props.original
                                    )}
                                >
                                    {findLookupValue(
                                        "CommitteeMemberRole",
                                        "committeeMemberRoleTypeId",
                                        props.value?.find((x) => !x.dateCeased)
                                            ?.committeeMemberRoleTypeId,
                                        lookups
                                    )}
                                </span>
                            ),
                        },
                        {
                            id: "personnel.display",
                            Header: "",
                            accessor: "personnel.display",
                            maxWidth: 0,
                            Cell: (_props) => <></>,
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: CommitteeMembersPageProps
): ICommitteeMembersPageProps => {
    let committee: Dtos.Committee | undefined = !(
        state.committees.formData instanceof Array
    )
        ? state.committees.formData
        : undefined;
    let committeeMembers: Dtos.CommitteeMember[] | undefined =
        state.committeeMember.formData instanceof Array
            ? state.committeeMember.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        history: ownProps.history,
        location: ownProps.location,
        match: ownProps.match,

        committeeMembers: committeeMembers,
        formFilter: state.trials.formFilter,
        lookups: state.committeeMember.lookups,

        loadingCommitteeMembers:
            state.committeeMember.loadState &&
            state.committeeMember.loadState.status == RequestState.Pending,
        loadCommitteeMembersSuccess:
            state.committeeMember.loadState &&
            state.committeeMember.loadState.status == RequestState.Success,
        loadCommitteeMembersFailure:
            state.committeeMember.loadState &&
            state.committeeMember.loadState.status == RequestState.Failure,
        loadCommitteeMembersErrorCode:
            state.committeeMember.loadState.errorCode,

        committeeId:
            ownProps.match &&
            ownProps.match.params.committeeId.toString() != "create"
                ? ownProps.match.params.committeeId
                : undefined!,
        committee: committee!,

        loadingCommittee:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Pending,
        loadCommitteeSuccess:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Success,
        loadCommitteeFailure:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Failure,
        loadCommitteeErrorCode: state.committees.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,

        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: CommitteeMembersPageProps
): ICommitteeMembersPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadCommitteeMembers: bindActionCreators(CommitteeMemberActions.LoadCommitteeMemberByCommitteeId, dispatch),
        clearCommitteeMembers: bindActionCreators(CommitteeMemberActions.Clear, dispatch),
        loadCommittee: bindActionCreators(CommitteeActions.LoadCommitteeById, dispatch),
        clearCommittee: bindActionCreators(CommitteeActions.Clear, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default RouterHoc<
    ICommitteeMembersPageParams,
    CommitteeMembersPageProps
>(
    connect(mapStateToProps, mapDispatchToProps)(
        CommitteeMembersPage
    ) as unknown as ComponentType<CommitteeMembersPageProps>
) as ComponentType<any>;
